import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import {
  ModalBodyComponent,
  ModalComponent,
  ModalFooterAction,
  ModalFooterComponent,
} from '@groundworks/ngx-groundworks-ui';
import {
  AccountCreditRequestTypeInfo,
  AmountSignConstraint,
  ApproveAccountCreditRequestDto,
  PostAccountCreditRequestDto,
  AttachmentSectionInfo,
  EditAccountCreditRequestDto,
  PendingCreditRequestInfo,
  SubmitAccountCreditRequestDto,
} from '../../sdk/sdk';
import { AccountCreditRequestService } from '../../services/account-credit-request.service';
import { AttachmentService } from '../../services/attachment.service';
import { ModalService } from '../../services/modal.service';
import { apiErrorMessage } from '../../utils/api-error-message';
import {
  parseCurrencyAmount,
  rejectAllCurrency,
  requiredNegativeCurrency,
  requiredNonZeroCurrency,
  requiredPositiveCurrency,
} from '../../utils/form-validators';
import { formatLocalIsoDate } from '../../utils/iso-date';
import { FileDropzoneComponent } from '../file-dropzone/file-dropzone.component';
import { PaginatedModalHeaderComponent } from '../paginated-modal-header/paginated-modal-header.component';

const MAX_DOCUMENT_BYTES = 2 * 1024 * 1024;

@Component({
  selector: 'app-account-credit-request-modal',
  imports: [
    ReactiveFormsModule,
    NgSelectComponent,
    ModalComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    FileDropzoneComponent,
    PaginatedModalHeaderComponent,
  ],
  templateUrl: './account-credit-request-modal.component.html',
  styleUrl: './account-credit-request-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Injected rather than piped in the template: the banner is one computed sentence whose "by
  // {name}" clause disappears when the approver is unresolvable, which is awkward to express as
  // interpolation.
  providers: [DatePipe],
})
export class AccountCreditRequestModalComponent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly formBuilder = inject(FormBuilder);
  private readonly acrService = inject(AccountCreditRequestService);
  private readonly attachmentService = inject(AttachmentService);
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly datePipe = inject(DatePipe);

  readonly opportunityId = signal<string | null>(null);
  readonly jobId = signal<string | null>(null);
  readonly bcJobNumber = signal<string | null>(null);
  readonly attachmentSections = signal<AttachmentSectionInfo[]>([]);
  readonly acrTypes = signal<AccountCreditRequestTypeInfo[]>([]);
  readonly documents = signal<File[]>([]);
  readonly oversizedFileNames = signal<string[]>([]);
  readonly loading = signal(false);
  readonly deleting = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly reviewMode = signal(false);
  readonly pendingCreditRequests = signal<PendingCreditRequestInfo[]>([]);
  readonly currentAcrIndex = signal(0);
  readonly currentAcr = computed(() => this.pendingCreditRequests()[this.currentAcrIndex()]);
  readonly totalPendingAcrs = computed(() => this.pendingCreditRequests().length);
  // The fetched list omits soft-deleted types, so a request created before its type was retired
  // would open with an empty selection. Append that request's own type (carried on the pending
  // payload) so it renders and can be saved unchanged. New requests never get the extra option.
  readonly selectableAcrTypes = computed(() => {
    const types = this.acrTypes();
    const currentType = this.currentAcr()?.type;
    if (!currentType || types.some(type => type.id === currentType.id)) return types;
    return [...types, currentType];
  });
  readonly isFirstAcr = computed(() => this.currentAcrIndex() === 0);
  readonly isLastAcr = computed(() => this.currentAcrIndex() === this.totalPendingAcrs() - 1);
  readonly acrPositionLabel = computed(() => `${this.currentAcrIndex() + 1}/${this.totalPendingAcrs()}`);

  private static readonly ACCOUNTING_SECTION_NAME = 'Accounting';

  // job.Touchpoints.Content is nvarchar(2000) and every ACR touchpoint now opens with an action
  // sentence before the note, so the note must leave room for it. Mirrors MaxNotesLength in
  // AccountCreditRequestsEndpoints, which rejects anything longer rather than truncating an
  // audit note.
  readonly maxNotesLength = 1900;

  readonly form: FormGroup = this.formBuilder.group({
    acrType: [null as AccountCreditRequestTypeInfo | null, Validators.required],
    creditAmount: [null as number | null, requiredNonZeroCurrency],
    // Validated rather than capped with a maxlength attribute: the browser enforces maxlength by
    // silently discarding the tail of a pasted note, which is the one outcome an audit trail cannot
    // afford. Over-long notes keep every character and block the action until the user trims them.
    notes: ['', Validators.maxLength(this.maxNotesLength)],
  });

  private readonly notesValue = toSignal(
    this.form.controls['notes'].valueChanges as Observable<string | null>,
    { initialValue: '' }
  );

  readonly notesCharacterCount = computed(() => (this.notesValue() ?? '').length);

  readonly notesTooLong = computed(() => this.notesCharacterCount() > this.maxNotesLength);

  get title(): string {
    return this.reviewMode() ? `Review ACR ${this.acrPositionLabel()}` : 'Account Credit Request';
  }

  readonly isApprover = computed(() => this.currentAcr()?.canApprove ?? false);
  readonly canEdit = computed(() => this.currentAcr()?.canEdit ?? false);

  // An approved refund with no payment yet. Frozen for everyone; only Accounting can act on it.
  // Type and amount lock via the existing `reviewMode() && !canEdit()` bindings, since canEdit is
  // false for these; notes and upload stay live so Accounting can record how the refund was issued.
  readonly awaitingAccountingPost = computed(() => this.currentAcr()?.awaitingAccountingPost ?? false);
  readonly canPost = computed(() => this.currentAcr()?.canPost ?? false);
  // Outside review mode there is no request to delete yet, so the flag only governs review.
  readonly canDelete = computed(() => this.currentAcr()?.canDelete ?? true);

  // The server could not check the caller's group membership, so every capability flag above
  // defaulted to false rather than being genuinely denied. Without this the footer would simply be
  // empty — identical to a user who legitimately may do nothing — and the only trace of the outage
  // would be a LogWarning nobody reading a bug report can see.
  readonly capabilitiesUnavailable = computed(() => this.currentAcr()?.capabilitiesUnavailable ?? false);

  // Only worth saying when it actually cost the user something to act on. A read-only view with no
  // actions to offer anyway does not need an apology for actions it was not going to show.
  readonly showCapabilitiesUnavailable = computed(
    () => this.reviewMode() && this.capabilitiesUnavailable() && this.footerActions().length === 0);

  // The approval stamp, for the banner an approved refund shows. approvedByName is resolved
  // server-side — approvedBy itself is a raw user id — and comes back empty when the id matches no
  // person, which is why the "by {name}" clause is conditional rather than always rendered.
  readonly approvedByName = computed(() => this.currentAcr()?.approvedByName ?? null);
  readonly approvedAt = computed(() => this.currentAcr()?.approvedAt ?? null);

  readonly approvalStamp = computed(() => {
    const by = this.approvedByName() ? ` by ${this.approvedByName()}` : '';
    const approvedAt = this.approvedAt();
    const on = approvedAt ? ` on ${this.datePipe.transform(approvedAt, 'M/d/yyyy')}` : '';
    return `Approved${by}${on} — awaiting Accounting post`;
  });

  private readonly showPrimaryAction = computed(
    () => !this.reviewMode() || this.isApprover() || this.canEdit() || this.canPost());

  readonly footerActions = computed<ModalFooterAction[]>(() => {
    const actions: ModalFooterAction[] = [];
    if (this.reviewMode() && this.canDelete()) {
      actions.push({
        label: 'Delete',
        type: 'outline-danger',
        action: () => this.deleteAccountCreditRequest(),
        loading: () => this.deleting(),
        // Not gated on form.invalid — a reviewer may deny a request without a valid type or amount —
        // so the notes cap has to gate this button on its own.
        disabled: () => this.loading() || this.deleting() || this.notesTooLong(),
      });
    }
    if (this.showPrimaryAction()) {
      actions.push({
        label: this.submitLabel(),
        type: 'primary',
        action: () => this.confirm(),
        loading: () => this.loading(),
        disabled: () => this.form.invalid || this.loading() || this.deleting(),
      });
    }
    return actions;
  });

  private static readonly DEFAULT_SUBMIT_LABEL = 'Submit Request';
  private readonly submitLabelRules: { when: () => boolean; label: string }[] = [
    { when: () => this.reviewMode() && this.canPost(), label: 'Post' },
    { when: () => this.reviewMode() && this.isApprover(), label: 'Approve Request' },
    { when: () => this.reviewMode() && this.canEdit(), label: 'Update' },
  ];

  private submitLabel(): string {
    return this.submitLabelRules.find(rule => rule.when())?.label
      ?? AccountCreditRequestModalComponent.DEFAULT_SUBMIT_LABEL;
  }

  constructor() {
    this.acrService.getCreditRequestTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(types => this.acrTypes.set(types));

    this.attachmentService.getAttachmentSections()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(sections => this.attachmentSections.set(sections));

    this.form.controls['acrType'].valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((type: AccountCreditRequestTypeInfo | null) =>
        this.applySignValidator(type?.signConstraint));

    effect(() => {
      const acr = this.currentAcr();
      const types = this.selectableAcrTypes();
      if (!acr) return;
      const selectedType = types.find(type => type.id === acr.paymentMethodId) ?? null;
      this.form.controls['acrType'].setValue(selectedType, { emitEvent: false });
      this.applySignValidator(selectedType?.signConstraint);
      this.form.controls['creditAmount'].setValue(
        acr.creditAmount != null ? acr.creditAmount.toFixed(2) : null,
        { emitEvent: false },
      );
    });
  }

  readonly selectedSignConstraint = signal<AmountSignConstraint | undefined>(undefined);

  private static readonly SIGN_REQUIREMENT_MESSAGES: Record<string, string> = {
    [AmountSignConstraint.PositiveOnly]: 'Amount must be positive.',
    [AmountSignConstraint.NegativeOnly]: 'Amount must be negative.',
    [AmountSignConstraint.Either]: 'Amount must be non-zero.',
  };

  creditAmountErrorMessage(): string {
    const raw = this.form.controls['creditAmount'].value;
    if (raw == null || raw === '') return 'Amount is required.';
    return this.describeSignRequirement(this.selectedSignConstraint());
  }

  private describeSignRequirement(constraint: AmountSignConstraint | undefined): string {
    if (constraint === undefined) {
      return AccountCreditRequestModalComponent.SIGN_REQUIREMENT_MESSAGES[AmountSignConstraint.Either];
    }
    return AccountCreditRequestModalComponent.SIGN_REQUIREMENT_MESSAGES[constraint]
      ?? 'This amount sign is not supported.';
  }

  private applySignValidator(constraint: AmountSignConstraint | undefined): void {
    this.selectedSignConstraint.set(constraint);
    const control = this.form.controls['creditAmount'];
    control.setValidators(this.validatorForConstraint(constraint));
    control.updateValueAndValidity({ emitEvent: false });
  }

  private validatorForConstraint(constraint: AmountSignConstraint | undefined): ValidatorFn {
    switch (constraint) {
      case AmountSignConstraint.PositiveOnly:
        return requiredPositiveCurrency;
      case AmountSignConstraint.NegativeOnly:
        return requiredNegativeCurrency;
      case AmountSignConstraint.Either:
      case undefined:
        return requiredNonZeroCurrency;
      default:
        return rejectAllCurrency;
    }
  }

  setPendingCreditRequests(requests: PendingCreditRequestInfo[]): void {
    this.pendingCreditRequests.set(requests);
    this.currentAcrIndex.set(0);
  }

  previousAcr(): void {
    if (this.isFirstAcr()) return;
    this.currentAcrIndex.update(i => i - 1);
    this.resetPerAcrInputs();
  }

  nextAcr(): void {
    if (this.isLastAcr()) return;
    this.currentAcrIndex.update(i => i + 1);
    this.resetPerAcrInputs();
  }

  private resetPerAcrInputs(): void {
    // Emits deliberately: notesCharacterCount is derived from this control's valueChanges, so
    // suppressing the event would leave the counter showing the previous ACR's length over an
    // empty box. Nothing else subscribes to notes.
    this.form.controls['notes'].setValue('');
    this.documents.set([]);
    this.oversizedFileNames.set([]);
    this.errorMessage.set(null);
  }

  onFilesSelected(files: File[]): void {
    const accepted: File[] = [];
    const oversized: string[] = [];
    for (const file of files) {
      if (file.size > MAX_DOCUMENT_BYTES) {
        oversized.push(file.name);
      } else {
        accepted.push(file);
      }
    }
    if (accepted.length > 0) this.documents.update(docs => [...docs, ...accepted]);
    this.oversizedFileNames.set(oversized);
  }

  removeDocument(index: number): void {
    this.documents.update(docs => docs.filter((_, i) => i !== index));
  }

  onCreditAmountBlur(): void {
    const control = this.form.controls['creditAmount'];
    const raw = control.value;
    if (raw == null || raw === '') return;
    const num = parseCurrencyAmount(raw);
    if (!Number.isFinite(num)) {
      control.setValue(null);
      return;
    }
    control.setValue(num.toFixed(2));
  }

  confirm(): void {
    if (this.form.invalid) return;
    if (this.reviewMode()) {
      // Post is checked first: an approved refund is neither approvable nor editable, so the other
      // two branches are already false for it, but ordering makes the precedence explicit.
      if (this.canPost()) {
        this.postAccountCreditRequest();
      } else if (this.isApprover()) {
        this.approveAccountCreditRequest();
      } else if (this.canEdit()) {
        this.editAccountCreditRequest();
      }
      return;
    }
    this.submitAccountCreditRequest();
  }

  private submitAccountCreditRequest(): void {
    const acrType = this.form.value.acrType as AccountCreditRequestTypeInfo | null;
    if (!acrType) return;
    const dto = new SubmitAccountCreditRequestDto({
      opportunityId: this.opportunityId() ?? undefined,
      jobId: this.jobId() ?? undefined,
      creditAmount: parseCurrencyAmount(this.form.value.creditAmount),
      paymentMethodId: acrType.id,
      notes: this.form.value.notes as string,
    });

    this.submitWithUpload(
      this.acrService.submitAccountCreditRequest(dto),
      'Failed to submit account credit request. Please try again.',
      (uploadError) => this.closeAfterAcr(uploadError),
    );
  }

  private approveAccountCreditRequest(): void {
    const creditRequestId = this.currentAcr()?.id;
    if (!creditRequestId) return;
    const dto = new ApproveAccountCreditRequestDto({
      paymentDate: new Date(formatLocalIsoDate(new Date())),
      notes: this.form.value.notes as string,
    });

    this.submitWithUpload(
      this.acrService.approveAccountCreditRequest(creditRequestId, dto),
      'Failed to approve the account credit request. Please try again.',
      (uploadError, result) => this.closeAfterApproval(uploadError, result.awaitingAccountingPost),
    );
  }

  private postAccountCreditRequest(): void {
    const creditRequestId = this.currentAcr()?.id;
    if (!creditRequestId) return;
    const dto = new PostAccountCreditRequestDto({
      // The poster's local day: the payment is dated when Accounting issued the refund, which can be
      // days after the approval.
      paymentDate: new Date(formatLocalIsoDate(new Date())),
      notes: this.form.value.notes as string,
    });

    this.submitWithUpload(
      this.acrService.postAccountCreditRequest(creditRequestId, dto),
      'Failed to post the account credit request. Please try again.',
      (uploadError) => this.closeAfterPost(uploadError),
      // The payment is already recorded by the time the upload runs. Say so, or the message reads as
      // if the post failed and the user tries again.
      'The refund payment was posted',
    );
  }

  private editAccountCreditRequest(): void {
    const creditRequestId = this.currentAcr()?.id;
    const acrType = this.form.value.acrType as AccountCreditRequestTypeInfo | null;
    if (!creditRequestId || !acrType) return;
    const dto = new EditAccountCreditRequestDto({
      creditAmount: parseCurrencyAmount(this.form.value.creditAmount),
      paymentMethodId: acrType.id,
      notes: this.form.value.notes as string,
    });

    this.submitWithUpload(
      this.acrService.editAccountCreditRequest(creditRequestId, dto),
      'Failed to update the account credit request. Please try again.',
      (uploadError) => this.closeAfterEdit(uploadError),
    );
  }

  private beginSubmit(): void {
    this.errorMessage.set(null);
    this.loading.set(true);
  }

  // Generic in the operation's result so a caller that needs the response — approve, which must know
  // whether a payment was actually posted — can read it in its close callback. The branching belongs
  // there, not here: this method stays ignorant of ACR semantics.
  private submitWithUpload<TResult>(
    operation$: Observable<TResult>,
    failMessage: string,
    onClose: (uploadError: string | null, result: TResult) => void,
    savedDescription?: string,
  ): void {
    this.beginSubmit();
    operation$
      .pipe(
        catchError((err) => this.fail(apiErrorMessage(err, failMessage, 'Account credit request write failed'))),
        switchMap((result) =>
          this.uploadDocuments(savedDescription).pipe(map((uploadError) => ({ result, uploadError })))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ result, uploadError }) => onClose(uploadError, result));
  }

  // savedDescription names what DID succeed. The default speaks of the request being saved, which is
  // wrong after a post: the payment was recorded and the money is out, and telling someone their
  // $12k refund "was saved, but..." reads as a failure and invites them to post it a second time.
  private uploadDocuments(savedDescription = 'Credit request was saved'): Observable<string | null> {
    const opportunityId = this.opportunityId();
    const files = this.documents();
    if (!opportunityId || files.length === 0) return of(null);

    const accountingSectionId = this.attachmentSections()
      .find((s) => s.name === AccountCreditRequestModalComponent.ACCOUNTING_SECTION_NAME)?.id ?? null;
    if (!accountingSectionId) {
      console.error('ACR document upload skipped: Accounting attachment section not found');
      return of(`${savedDescription}, but the Accounting attachment section was not found.`);
    }

    const uploads = files.map((file) =>
      this.attachmentService.uploadAttachment(
        opportunityId,
        { data: file, fileName: file.name },
        null,
        null,
        accountingSectionId
      )
    );

    return forkJoin(uploads).pipe(
      map(() => null),
      catchError((err) => {
        console.error('ACR document upload failed', err);
        return of(`${savedDescription}, but uploading one or more documents failed.`);
      }),
    );
  }

  private fail(message: string): Observable<never> {
    this.loading.set(false);
    this.errorMessage.set(message);
    return EMPTY;
  }

  private closeModal(): void {
    this.activeModal.close();
  }

  private closeAfterAcr(uploadError: string | null): void {
    this.closeModal();
    if (uploadError) {
      this.modalService.openErrorModal('Account Credit Request', uploadError);
    } else {
      this.modalService.openAcrSuccessModal(parseCurrencyAmount(this.form.value.creditAmount), this.bcJobNumber());
    }
  }

  private closeAfterEdit(uploadError: string | null): void {
    this.closeModal();
    if (uploadError) {
      this.modalService.openErrorModal('Account Credit Request', uploadError);
    } else {
      this.modalService.openAcrEditSuccessModal(parseCurrencyAmount(this.form.value.creditAmount), this.bcJobNumber());
    }
  }

  // Approving a refund posts no payment — it hands the request to Accounting, who post it on the day
  // they issue it. Only the single-step path (financial adjustments) really creates one, so the
  // payment confirmation is wrong for the other and the two get separate copy.
  private closeAfterApproval(uploadError: string | null, awaitingAccountingPost: boolean): void {
    this.closeModal();
    if (uploadError) {
      this.modalService.openErrorModal('Review ACR', uploadError);
      return;
    }
    const amount = Number(this.form.value.creditAmount);
    if (awaitingAccountingPost) {
      this.modalService.openAcrAwaitingPostModal(amount, this.bcJobNumber());
    } else {
      this.modalService.openPaymentSuccessModal(amount, this.bcJobNumber());
    }
  }

  // Posting is where the payment genuinely is created, so this one is unconditional.
  private closeAfterPost(uploadError: string | null): void {
    this.closeModal();
    if (uploadError) {
      this.modalService.openErrorModal('Review ACR', uploadError);
    } else {
      this.modalService.openPaymentSuccessModal(Number(this.form.value.creditAmount), this.bcJobNumber());
    }
  }

  deleteAccountCreditRequest(): void {
    const creditRequestId = this.currentAcr()?.id;
    if (!creditRequestId) return;

    const confirmRef = this.modalService.openConfirmModal(
      'Delete Account Credit Request',
      'Are you sure you want to delete this account credit request?',
      'Delete',
      'alert-danger',
    );

    confirmRef.result
      .then((confirmed: boolean) => {
        if (confirmed) this.performDelete(creditRequestId);
      })
      .catch(() => {
        // Confirmation dismissed — nothing to do.
      });
  }

  private performDelete(creditRequestId: string): void {
    this.errorMessage.set(null);
    this.deleting.set(true);

    this.acrService.deleteAccountCreditRequest(creditRequestId, this.form.value.notes as string)
      .pipe(
        catchError((err) => this.failDelete(apiErrorMessage(
          err, 'Failed to delete the account credit request. Please try again.',
          'Account credit request delete failed'))),
        switchMap(() => this.uploadDocuments('The account credit request was deleted')),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((uploadError) => this.closeAfterDelete(uploadError));
  }

  private failDelete(message: string): Observable<never> {
    this.deleting.set(false);
    this.errorMessage.set(message);
    return EMPTY;
  }

  private closeAfterDelete(uploadError: string | null): void {
    this.deleting.set(false);
    this.closeModal();
    if (uploadError) {
      this.modalService.openErrorModal('Delete Account Credit Request', uploadError);
    } else {
      this.modalService.openAcrDeleteSuccessModal(Number(this.form.value.creditAmount), this.bcJobNumber());
    }
  }

  cancel(): void {
    this.activeModal.dismiss();
  }
}
